import {
  ListRequestParams,
  listRequest,
  requestRaw,
  makeDefaultApi,
  requestWithValidation,
  listParamsToQueryString,
  request,
  RemoveResult,
  requestBulkRemoveEntities,
  GetOptions,
  RequestOptions,
  ValidationResult,
} from './request';
import { DimensionData, DimensionStageData } from './dimensions';
import { CropData } from './crops';
import { BreederData } from './organizations';
import { NameI18nData } from './names';

export interface PartnerCropVarietyData extends DimensionData {
  crop_id: CropData;
}

export interface ReplacedVarietyData {
  id: string;
  name: string;
  code: string;
}

export interface CropVarietyData extends DimensionData {
  crop_id: CropData;
  variety_type: NameI18nData;
  origin: BreederData;
  origin_type?: NameI18nData; // read-only
  origin_2: BreederData;
  origin_2_type?: NameI18nData; // read-only
  crop_editable?: boolean; // returned only by retrieve
  parent_1: NameI18nData; // returned only by retrieve
  parent_2: NameI18nData; // returned only by retrieve
  replacing?: ReplacedVarietyData[]; // read-only, returned only by retrieve
  replaced_by?: ReplacedVarietyData | null; // read-only, returned only by retrieve
}

export interface CropVarietyListRequestParams extends ListRequestParams {
  ids?: string[];
  dataset_id?: string;
  crop_ids?: string[];
  sort_by: string;
  archived?: boolean;
}

export interface CropVarietyStageItemData {
  crop_variety_id: string;
  stage_id: string;
  start_at: string;
  external: boolean;
  comment: string;
}

export interface CropVarietyMultiStageData {
  country_id: string;
  items: CropVarietyStageItemData[];
}

const cropVarietiesApi = makeDefaultApi<CropVarietyData, CropVarietyListRequestParams>('crop_varieties');

export const { retrieve, save, remove } = cropVarietiesApi;

/** Wrapper that adds default parameters to the list request */
export function list(
  params: ListRequestParams & CropVarietyListRequestParams & { type?: string },
  options?: GetOptions,
  requestOptions?: RequestOptions
): Promise<CropVarietyData[]> {
  const defaultParams: CropVarietyListRequestParams = {
    sort_by: 'anonymized_code',
    archived: false,
  };
  return cropVarietiesApi.list({ ...defaultParams, ...params }, options, requestOptions);
}

export function addCVStage(id: string, data: DimensionStageData) {
  return requestWithValidation('POST', '/api/crop_varieties/' + id + '/add_stage/', data);
}

/**
 * Merge one crop variety into another, archiving the duplicate.
 *
 * @param canonicalId Id of the variety that survives the merge.
 * @param duplicateId Id of the variety to archive.
 */
export function replace(
  canonicalId: string,
  duplicateId: string
): Promise<ValidationResult<ReplacedVarietyData>> {
  return requestWithValidation('POST', '/api/crop_varieties/' + canonicalId + '/replace/', {
    duplicate: duplicateId,
  });
}

export function undoReplacement(id: string, duplicateId: string): Promise<void> {
  return request('POST', '/api/crop_varieties/' + id + '/undo_replacement/', {
    duplicate: duplicateId,
  });
}

export function undoAllReplacements(id: string): Promise<void> {
  return request('POST', '/api/crop_varieties/' + id + '/undo_all_replacements/', {});
}

export function updateStages(data: CropVarietyMultiStageData): Promise<void> {
  return request('POST', '/api/crop_varieties/update_stages/', data);
}

/** What the React table's requests carry, on top of the flat parameters the legacy screen sends. */
export interface CropVarietyGridRequestParams extends ListRequestParams {
  /** A serialised filter model, or '' when nothing is filtered. */
  filter?: string;
  /** Sort keys, outermost first, '-' prefixed for descending. */
  sort?: string[];
}

/**
 * One page for the React table.
 *
 * Not `list`: that one defaults `sort_by` and `archived` for the legacy screen, and the table
 * decides both itself — archived varieties are one of its filter conditions, and its sort keys
 * are what orders the page.
 */
export function listForGrid(
  params: CropVarietyGridRequestParams,
  requestOptions?: RequestOptions
): Promise<CropVarietyData[]> {
  return listRequest('/api/crop_varieties/', params, requestOptions);
}

export function downloadExport(filters: {
  crop_ids?: string[];
  /** The table's own conditions, so an export matches the rows it was asked for. */
  filter?: string;
  name_prefix?: string;
}): Promise<Blob> {
  return requestRaw('GET', '/api/crop_varieties/export/?' + listParamsToQueryString(filters));
}

export function bulkRemove(params: {}): Promise<RemoveResult> {
  const queryParameters = params ? '?' + listParamsToQueryString(params) : '';
  return requestBulkRemoveEntities(`/api/crop_varieties/bulk_remove/${queryParameters}`);
}
